import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import PublicCustomerMenu from './PublicCustomerMenu';
import { clearAuthState } from '../utils/tokenStorage';
import { setMpinSet, setCustomerName, logout } from '../store/authSlice';
import { customerDisplayName, customerIdentity } from '../utils/customerAccount';
import { resolveAssetUrl } from '../utils/media';
import api from '../services/api';
import './ShopHeaders.css';

export default function LandingCustomerAccount({ palette, open, onToggle }) {
  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [mpinOpen, setMpinOpen] = useState(false);
  const [form, setForm] = useState({ current: '', code: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const user = auth.user;
  const customerId = ['CUSTOMER', 'USER'].includes(user?.role) ? user.id : null;
  useEffect(() => {
    if (!customerId) return undefined;
    let active = true;
    api.get('/customer-info/me').then(({ data }) => {
      const profile = data.customerInfo || data.profile || data;
      const name = customerDisplayName(profile?.name).trim();
      if (active && name) dispatch(setCustomerName(name));
    }).catch(() => { /* Keep the authenticated identity if the profile is temporarily unavailable. */ });
    return () => { active = false; };
  }, [customerId, dispatch]);
  if (!user || !['CUSTOMER', 'USER'].includes(user.role)) return null;
  const { name, initial } = customerIdentity(user);
  const contact = /@placeholder\.local$/i.test(user.email || '') ? user.phone : user.email || user.phone;
  const percent = Number(user.profileCompletionPercent);
  const completion = Number.isFinite(percent) ? Math.max(0, Math.min(100, Math.round(percent))) : user.profileCompleted ? 100 : 0;
  const signOut = () => {
    // Capture before clearing storage; revoke this session on the server too.
    const refreshToken = auth.refreshToken;
    if (refreshToken) api.post('/auth/logout', { refreshToken }, { skipAuthError: true }).catch(() => {});
    clearAuthState(); dispatch(logout()); onToggle(false);
  };
  const saveMpin = async event => {
    event.preventDefault();
    if (busy) return;
    if (!/^\d{6}$/.test(form.code) || form.code !== form.confirm) { setError('Enter and confirm the same six-digit MPIN.'); return; }
    setBusy(true); setError('');
    try {
      await api.post('/auth/mpin', { mpin: form.code, currentMpin: user.mpinSet ? form.current : undefined });
      dispatch(setMpinSet(true));
      setMpinOpen(false); setForm({ current: '', code: '', confirm: '' });
    } catch (err) { setError(err.response?.data?.message || 'Unable to save MPIN. Please try again.'); }
    finally { setBusy(false); }
  };
  return <>
    <PublicCustomerMenu open={open} onToggle={onToggle} name={name} contact={contact}
      initial={initial}
      avatarUrl={resolveAssetUrl(user.profilePhotoUrl || user.avatarUrl || user.profileImageUrl)} completion={completion}
      palette={palette} mpinSet={user.mpinSet} onProfile={() => navigate('/customer/profile')}
      onOrders={() => navigate('/customer/orders')} onPassword={() => navigate('/auth/reset')}
      onMpin={() => { setForm({ current: '', code: '', confirm: '' }); setError(''); setMpinOpen(true); }} onLogout={signOut} />
    <Modal show={mpinOpen} onHide={() => setMpinOpen(false)} centered>
      <Modal.Header closeButton closeVariant="white" style={{ background: palette.primary, color: palette.onPrimary || '#fff' }}><Modal.Title>{user.mpinSet ? 'Change MPIN' : 'Set MPIN'}</Modal.Title></Modal.Header>
      <Form onSubmit={saveMpin}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {(user.mpinSet ? ['current', 'code', 'confirm'] : ['code', 'confirm']).map(key => <Form.Group key={key} className="mb-3" controlId={`landing-mpin-${key}`}>
            <Form.Label>{key === 'current' ? 'Current MPIN' : key === 'code' ? 'New MPIN' : 'Confirm MPIN'}</Form.Label>
            <Form.Control type="password" inputMode="numeric" autoComplete={key === 'current' ? 'current-password' : 'new-password'} required pattern="[0-9]{6}" maxLength={6} disabled={busy} value={form[key]} onChange={event => setForm(previous => ({ ...previous, [key]: event.target.value.replace(/\D/g, '') }))} />
          </Form.Group>)}
        </Modal.Body>
        <Modal.Footer><Button variant="outline-secondary" onClick={() => setMpinOpen(false)}>Cancel</Button><Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save MPIN'}</Button></Modal.Footer>
      </Form>
    </Modal>
  </>;
}
